import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGetLoginPrivilegeInfo, useGetMenu } from '../../api/auth/auth';
import { GetLoginPrivilegeInfoParams } from '../../api/model';
import { useGetPsCheckApproveCount, useGetPsCheckCheckCount } from '../../api/ps-checks/ps-checks';
import { useDefaultOptions } from '../../hooks/api';
import { getPageClass } from '../../utils/pathname';
import Link from '../atoms/Link';
import BmsImportModal from '../pages/bmsimport/BmsImportModal';
import { getIsAccess } from '../pages/ps/index/PsListCommmon';
import SlideToggle from './SlideToggle';

interface SubMenuItemProps {
  label: DefaultTFuncReturn;
  to: string;
  hover: boolean;
  disabled: boolean;
}

/**
 * サイドメニューのサブメニュー項目
 * @param param0
 * @returns
 */
function SubMenuItem({ label, to, hover, disabled }: SubMenuItemProps) {
  return (
    <li className={disabled ? 'disabled' : undefined}>
      <i className="fa-solid fa-caret-right" />
      {hover && (
        <Link to={to} className={disabled ? 'disabled-link' : undefined} tabIndex={disabled ? -1 : undefined}>
          {label}
        </Link>
      )}
    </li>
  );
}

/**
 * サイドナビのコンポーネント
 * TODO: ユーザー権限に応じた表示
 * @returns
 */
function SideNavi() {
  // タブキーでサイドメニューが壊れないようにホバーした時だけ、HTML 全体が生成されるようにする
  // 注：モックではタブキーを押していくとサイドメニューがおかしくなる

  // サイドナビにマウスオーバーしているかどうか
  const [hover, setHover] = useState(true);

  // PS を開いているかどうか
  const [openPs, setOpenPs] = useState(false);

  // システム管理を開いているかどうか
  const [openSystemManagement, setOpenSystemManagement] = useState(false);

  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // データの取得
  const options = useDefaultOptions(language);

  // メニューの権限
  const { data: authData } = useGetMenu(options);

  // 確認申請件数
  const checkCount = useGetPsCheckCheckCount(options);

  // 承認申請件数
  const approveCount = useGetPsCheckApproveCount(options);

  const paramForCheck = { function_id: 44 } as GetLoginPrivilegeInfoParams;
  const paramForApproval = { function_id: 45 } as GetLoginPrivilegeInfoParams;
  // ログインユーザ情報取得
  const psLoginPrivilegeInfoCheck = useGetLoginPrivilegeInfo(paramForCheck, options);
  const psLoginPrivilegeInfoApproval = useGetLoginPrivilegeInfo(paramForApproval, options);
  // 確認申請タブを表示するかどうか
  const isCheckUser = getIsAccess(psLoginPrivilegeInfoCheck.data?.data.user_privileges ?? []);
  // 承認申請タブを表示するかどうか
  const isApprovalUser = getIsAccess(psLoginPrivilegeInfoApproval.data?.data.user_privileges ?? []);

  // 表示しているページの種類（どの項目を背景色赤にするか設定するため）
  const { pathname } = useLocation();
  const pageClass = getPageClass(pathname);

  // BMSインポートダイアログ表示
  const [showBmsImportModal, setShowBmsImportModal] = useState(false);
  const openBmsImportModal = useCallback(() => {
    setShowBmsImportModal(true);
  }, []);

  return (
    <div
      id="sidenavi"
      // マウスオーバーしている場合
      onMouseEnter={() => setHover(true)}
      // マウスオーバーが終わった場合
      onMouseLeave={() => {
        // マウスオーバーが終わった場合、全て閉じる
        setHover(false);
        setOpenPs(false);
        setOpenSystemManagement(false);
      }}
    >
      <div id="sidenavi-wf">
        <div id="sidenavi-in">
          <div id="mainnavi-in">
            <nav>
              <ul>
                <li
                  className={`${pageClass === 'home' ? 'current' : ''} ${
                    authData?.data.home === false ? 'disabled' : ''
                  }`}
                >
                  <span className="snavfr">
                    <span>
                      <i className="fas fa-home" style={{ cursor: 'default' }} />
                    </span>
                    {hover && (
                      <Link
                        to={`/${language}/home`}
                        className={authData?.data.home === false ? 'disabled-link' : undefined}
                        tabIndex={authData?.data.home === false ? -1 : undefined}
                      >
                        {t('Home')}
                      </Link>
                    )}
                  </span>
                </li>
                <li
                  className={`${pageClass === 'productions' ? 'current' : ''} ${
                    authData?.data.productions === false ? 'disabled' : ''
                  }`}
                >
                  <span className="snavfr">
                    <span>
                      <i className="fa-solid fa-list-check" style={{ cursor: 'default' }} />
                    </span>
                    {hover && (
                      <Link
                        to={`/${language}/productions/`}
                        className={authData?.data.productions === false ? 'disabled-link' : undefined}
                        tabIndex={authData?.data.productions === false ? -1 : undefined}
                      >
                        {t('制作一覧')}
                      </Link>
                    )}
                  </span>
                </li>
                <li
                  className={`${pageClass === 'schedulelist' ? 'current' : ''} ${
                    authData?.data.schedule === false ? 'disabled' : ''
                  }`}
                >
                  <span className="snavfr">
                    <span>
                      <i className="fa-solid fa-calendar-check" style={{ cursor: 'default' }} />
                    </span>
                    {hover && (
                      <Link
                        to={`/${language}/schedulelist`}
                        className={authData?.data.schedule === false ? 'disabled-link' : undefined}
                        tabIndex={authData?.data.schedule === false ? -1 : undefined}
                      >
                        {t('日程一覧')}
                      </Link>
                    )}
                  </span>
                </li>
                <li
                  className={`${(openPs ? 'active ' : '') + (pageClass === 'ps' ? 'current' : '')} ${
                    authData?.data.ps === false ? 'disabled' : ''
                  }`}
                >
                  <span className="snavfr">
                    <span>
                      <i className="fa-solid fa-book" style={{ cursor: 'default' }} />
                    </span>
                    {hover && (
                      <button
                        type="button"
                        className="text-button white-text"
                        // クリックされたら開く
                        onClick={() => setOpenPs((pre) => !pre)}
                      >
                        {t('PS一覧')}
                        <i className={`fa-solid ${openPs ? 'fa-chevron-up' : 'fa-chevron-down'} udarrow`} />
                      </button>
                    )}
                  </span>
                  <SlideToggle open={openPs}>
                    <ul style={{ display: 'block' }}>
                      <SubMenuItem
                        label={t('PS更新履歴')}
                        to={`/${language}/ps#1`}
                        hover={hover}
                        disabled={authData?.data.ps_1 === false}
                      />
                      <SubMenuItem
                        label={t('PS閲覧')}
                        to={`/${language}/ps/view`}
                        hover={hover}
                        disabled={authData?.data.ps_view === false}
                      />
                      <SubMenuItem
                        label={t('PS改廃案一覧')}
                        to={`/${language}/ps#2`}
                        hover={hover}
                        disabled={authData?.data.ps_2 === false}
                      />
                      {isCheckUser && (
                        <SubMenuItem
                          label={
                            !checkCount.isLoading &&
                            !checkCount.isError &&
                            checkCount.data.data != null &&
                            authData?.data.ps_3 === true
                              ? t('確認申請（{{count}}）', { count: checkCount.data.data } as FieldValues)
                              : t('確認申請')
                          }
                          to={`/${language}/ps#3`}
                          hover={hover}
                          disabled={authData?.data.ps_3 === false}
                        />
                      )}
                      {isApprovalUser && (
                        <SubMenuItem
                          label={
                            !approveCount.isLoading &&
                            !approveCount.isError &&
                            approveCount.data.data != null &&
                            authData?.data.ps_4 === true
                              ? t('承認申請（{{count}}）', { count: approveCount.data.data } as FieldValues)
                              : t('承認申請')
                          }
                          to={`/${language}/ps#4`}
                          hover={hover}
                          disabled={authData?.data.ps_4 === false}
                        />
                      )}
                    </ul>
                  </SlideToggle>
                </li>
                <li
                  className={`${pageClass === 'illusts' ? 'current' : ''} ${
                    authData?.data.illusts === false ? 'disabled' : ''
                  }`}
                >
                  <span className="snavfr">
                    <span>
                      <i className="fa-solid fa-paintbrush" style={{ cursor: 'default' }} />
                    </span>
                    {hover && (
                      <Link
                        to={`/${language}/illusts/`}
                        className={authData?.data.illusts === false ? 'disabled-link' : undefined}
                        tabIndex={authData?.data.illusts === false ? -1 : undefined}
                      >
                        {t('イラスト一覧')}
                      </Link>
                    )}
                  </span>
                </li>
                <li
                  className={`${openSystemManagement ? 'active' : ''} ${
                    authData?.data.system === false ? 'disabled' : ''
                  }`}
                >
                  <span className="snavfr">
                    <span>
                      <i className="fa-solid fa-gear" style={{ cursor: 'default' }} />
                    </span>
                    {hover && (
                      <button
                        type="button"
                        className="text-button white-text"
                        // クリックされたら開く
                        onClick={() => setOpenSystemManagement((pre) => !pre)}
                      >
                        {t('システム管理')}
                        <i
                          className={`fa-solid ${openSystemManagement ? 'fa-chevron-up' : 'fa-chevron-down'} udarrow`}
                        />
                      </button>
                    )}
                  </span>
                  <SlideToggle open={openSystemManagement}>
                    <ul style={{ display: 'block' }}>
                      <SubMenuItem
                        label={t('マスタ管理')}
                        to={`/${language}/masterslist`}
                        hover={hover}
                        disabled={authData?.data.system_master === false}
                      />
                      <SubMenuItem
                        label={t('ユーザー管理')}
                        to={`/${language}/users`}
                        hover={hover}
                        disabled={authData?.data.system_user === false}
                      />
                      <SubMenuItem
                        label={t('グループ管理')}
                        to={`/${language}/organizations`}
                        hover={hover}
                        disabled={authData?.data.system_group === false}
                      />
                      <SubMenuItem
                        label={t('権限管理')}
                        to={`/${language}/privileges`}
                        hover={hover}
                        disabled={authData?.data.system_privilage === false}
                      />
                      {/*  
                      <li className={authData?.data.system_bms === false ? 'disabled' : ''}>
                        <i className="fa-solid fa-caret-right" />
                        <span
                          onClick={() => {
                            if (authData?.data.system_bms !== false) {
                              void openBmsImportModal();
                            }
                          }}
                        >
                          {t('BMSインポート')}
                        </span>
                      </li>
                      */}
                      <SubMenuItem
                        label={t('マニュアル')}
                        to={`/${language}/manuals`}
                        hover={hover}
                        disabled={authData?.data.system_manual === false}
                      />
                      {authData?.data.system_log !== false && (
                        <SubMenuItem
                          label={t('ログ')}
                          to={`/${language}/logs`}
                          hover={hover}
                          disabled={authData?.data.system_privilage === false}
                        />
                      )}
                    </ul>
                  </SlideToggle>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
      <BmsImportModal active={showBmsImportModal} onClose={() => setShowBmsImportModal(false)} />
    </div>
  );
}

export default SideNavi;
